import { memo, useEffect, useState } from 'react'

import { useFlexOffers } from '@cox/core-ui8/dist/useFlexOffers'

import { useCountdown } from '../../../../hooks/useCountdown'
import { scrollToCarouselElementWithOffset } from '../../../../utils/helper-util'
import { getAttributeValue, hasCFConditionals } from '../../../../utils/rule-builder-util'
import { ImageryContentFragment } from '../../types'
import CarouselSidebar from '../CarouselSidebar/CarouselSidebar'
import CarouselSlide from '../CarouselSlide/CarouselSlide'
import { ImageCarouselProps } from '../types'

const ReactImageCarouselLargeScreen = (props: ImageCarouselProps) => {
  const {
    carouselItems,
    isInEditor,
    ruleExpression = '',
    ruleType,
    condition,
    tokenProperties,
    selectedSlideId,
    resetSlideId
  } = props
  const flexOffers = useFlexOffers(ruleExpression)
  const [activeItem, setActiveItem] = useState<string>()
  const [index, setIndex] = useState(0)
  const [pause, setPause] = useState(false)
  const [trigger, setTrigger] = useState(false)
  const slideIdMap = new Map()
  const duration = 5

  const getValue = (attribute: any, index: any, propertyName?: string) => {
    if (hasCFConditionals(attribute)) {
      const jsonAttribute = (props as any)?.[`${propertyName}_json`]
      return getAttributeValue(jsonAttribute, flexOffers, index, tokenProperties, propertyName)
    }
    return getAttributeValue(attribute, flexOffers, index, tokenProperties, propertyName)
  }

  const carouselItemIds: any = carouselItems.map((item: any, index) => {
    const slideIdToSet = item?.elements.contentFragmentId?.value
      ? getValue(item?.elements.contentFragmentId, index, 'contentFragmentId')
      : getValue(item?.id, index, 'id')
    return slideIdToSet
  })

  function getNewIndex(activeItem: any) {
    if (carouselItemIds?.indexOf(activeItem) > -1) {
      return carouselItemIds[carouselItemIds.indexOf(activeItem) + 1] !== undefined
        ? carouselItemIds.indexOf(activeItem) + 1
        : 0
    } else {
      return 0
    }
  }

  function getIndex(activeItem: any) {
    if (carouselItemIds?.indexOf(activeItem) > -1) {
      return carouselItemIds[carouselItemIds.indexOf(activeItem)] !== undefined ? carouselItemIds.indexOf(activeItem) : 0
    } else {
      return 0
    }
  }

  const { resetCounter, pauseCounter, resumeCounter } = useCountdown({
    initialCounter: duration,
    callback: (currentCounter: any) => {
      if (currentCounter === 0) {
        resetCounter()
        setTrigger(true)
      } else {
        setTrigger(false)
      }
    }
  })

  useEffect(() => {
    if (!isInEditor) {
      if (trigger) {
        const newIndex = getNewIndex(activeItem)
        setIndex(newIndex)
      }
    }
  }, [trigger])

  useEffect(() => {
    setActiveItem(carouselItemIds?.[index])
  }, [index, carouselItemIds])

  useEffect(() => {
    if (selectedSlideId) {
      const index = slideIdMap.get(selectedSlideId)
      if (index > -1) {
        scrollToCarouselElementWithOffset({}, '#', selectedSlideId)
        setIndex(index)
        resetCounter()
      }
    }
    return () => {
      resetSlideId?.()
    }
  }, [selectedSlideId])

  const handleOnClick = (id: string, e: any) => {
    e.preventDefault()
    goToSlide(id)
  }

  const goToSlide = (id: string) => {
    const newIndex = getIndex(id)
    setIndex(newIndex)
    resetCounter()
  }

  return (
    <div
      className='react-image-carousel-large-screen'
      onMouseOver={() => {
        setPause(true)
        pauseCounter()
      }}
      onMouseLeave={() => {
        setPause(false)
        resumeCounter()
      }}
    >
      <div className='image-carousel-large-item'>
        <div className='large-item-container'>
          {carouselItems?.map((item: ImageryContentFragment, index) => {
            if (index === 0 && flexOffers?.loading) {
              return <div className='react-image-container container image-placeholder skeleton-img-placeholder' />
            }
            const slideIdToSet = item?.elements.contentFragmentId?.value
              ? getValue(item?.elements.contentFragmentId, index, 'contentFragmentId')
              : getValue(item?.id, index, 'id')

            slideIdMap.set(slideIdToSet, index)
            return (
              <CarouselSlide
                key={`${item.id}_image${index}`}
                {...item}
                activeItem={activeItem}
                ruleExpression={ruleExpression}
                ruleType={ruleType}
                condition={condition}
                carouselSlideIndex={index}
                tokenProperties={tokenProperties}
                id={slideIdToSet}
              />
            )
          })}
          <CarouselSidebar
            carouselItems={carouselItems}
            activeItem={activeItem}
            handleOnClick={handleOnClick}
            duration={duration}
            pause={pause}
            ruleExpression={ruleExpression}
            ruleType={ruleType}
            condition={condition}
            tokenProperties={tokenProperties}
          />
        </div>
      </div>
    </div>
  )
}
export default memo(ReactImageCarouselLargeScreen)
